import React, { createContext, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppState } from '../app_state/AppStateContext.js';
import { container } from '../../shared/di/container.js';
import { AclService } from './AclService.js';
import type { AclCan } from './aclTypes.js';

interface AclContextValue {
  can: AclCan;
  isSuperAdmin: boolean;
  isLoading: boolean;
  refresh: () => void;
}

const AclContext = createContext<AclContextValue>({
  can: () => false,
  isSuperAdmin: false,
  isLoading: true,
  refresh: () => {},
});

export function AclProvider({ children }: { children: React.ReactNode }) {
  const { activeOrg, session } = useAppState();
  const orgId = activeOrg?.id ?? null;
  const uid = session?.uid ?? null;
  const queryClient = useQueryClient();

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['acl', 'org', orgId, uid],
    queryFn: () => container.user.getMyPermissionsUseCase({ orgId: orgId! }),
    enabled: !!orgId && !!uid,
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
  });

  const service = useMemo(() => new AclService(rules), [rules]);

  const can: AclCan = useCallback(
    (resource, action) => service.can(resource, action),
    [service],
  );

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['acl', 'org', orgId] });
  }, [queryClient, orgId]);

  const isSuperAdmin = !!(session?.isSuperAdmin);

  return (
    <AclContext.Provider value={{ can, isSuperAdmin, isLoading, refresh }}>
      <div data-acl-ready={!!orgId && !!uid && !isLoading ? 'true' : undefined} style={{ display: 'contents' }}>
        {children}
      </div>
    </AclContext.Provider>
  );
}

export { AclContext };
export type { AclContextValue };
